import { supabase } from '@/lib/supabase';
import type {
  CriterionType,
  UpdateCriterionInput,
} from '@/features/criteria/types';

// =========================================
// Queries (read operations)
// =========================================

/** Fetch all criteria for a user, optionally filtered by type and category */
export async function fetchCriteria(
  userId: string,
  options?: { type?: CriterionType; category?: string },
) {
  let query = supabase
    .from('criteria')
    .select('*')
    .eq('user_id', userId)
    .eq('is_template', false)
    .order('created_at', { ascending: false });

  if (options?.type) {
    query = query.eq('type', options.type);
  }

  if (options?.category) {
    query = query.eq('category', options.category);
  }

  return query;
}

/** Fetch a single criterion by ID */
export async function fetchCriterionById(criterionId: string) {
  return supabase
    .from('criteria')
    .select('*')
    .eq('id', criterionId)
    .single();
}

// =========================================
// Mutations (write operations)
// =========================================

/** Insert a new criterion */
export async function insertCriterion(params: {
  user_id: string;
  name: string;
  description: string | null;
  type: string;
  config: unknown;
  weight: number;
  category: string | null;
  is_template: boolean;
}) {
  return supabase
    .from('criteria')
    .insert(params)
    .select()
    .single();
}

/** Update an existing criterion by ID */
export async function updateCriterion(
  criterionId: string,
  updates: UpdateCriterionInput,
) {
  return supabase
    .from('criteria')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', criterionId)
    .select()
    .single();
}

/** Delete a criterion by ID */
export async function deleteCriterion(criterionId: string) {
  return supabase
    .from('criteria')
    .delete()
    .eq('id', criterionId);
}
